import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import StoryStatusBadge from '../../../components/abnahme/StoryStatusBadge';
import { STORY_STATUS_KLASSE } from '../../../lib/abnahme/statusKlasse';
import { STATUS_FARBE } from '../../../components/abnahme/statistik/viz';
import {
  abnahmeApi,
  STORY_STATUS_LABEL,
  type KatalogDto,
  type StoryDto,
  type StoryStatus,
} from '../../../lib/api/abnahme';
import { ABLAEUFE, AKTEUR_LABEL, type Ablauf, type AblaufSchritt, type Akteur } from '../../../lib/abnahme/ablaeufe';

/** Reihenfolge von „hält am meisten auf“ bis „fertig“ — ein Schritt ist so weit wie seine schwächste Story. */
const REIFE: StoryStatus[] = ['abgelehnt', 'entwurf', 'geplant', 'in_umsetzung', 'bereit_zur_abnahme', 'abgenommen'];

/** Für die Verteilungsbalken: Fertiges zuerst, damit der Fortschritt von links wächst. */
const BALKEN_REIHENFOLGE: StoryStatus[] = [
  'abgenommen',
  'bereit_zur_abnahme',
  'in_umsetzung',
  'geplant',
  'entwurf',
  'abgelehnt',
];

const AKTEUR_KLASSE: Record<Akteur, string> = {
  website: 'bg-violet-100 text-violet-900 dark:bg-violet-950 dark:text-violet-100',
  kunde: 'bg-violet-100 text-violet-900 dark:bg-violet-950 dark:text-violet-100',
  office: 'bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-100',
  lehrkraft: 'bg-orange-100 text-orange-900 dark:bg-orange-950 dark:text-orange-100',
  system: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100',
  admin: 'bg-zinc-100 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-100',
};

interface Stand {
  stories: StoryDto[];
  fehlend: string[];
  status: StoryStatus | null;
  kriterien: number;
  abgedeckt: number;
  rot: boolean;
  blocker: number;
}

/** Stand einer Menge von Stories: schwächster Status, Abdeckung, Warnungen. */
function standVon(keys: string[], stories: Map<string, StoryDto>): Stand {
  const gefunden = keys.map((key) => stories.get(key)).filter((s): s is StoryDto => Boolean(s));
  const fehlend = keys.filter((key) => !stories.has(key));
  const status = gefunden.length
    ? gefunden.map((s) => s.status).sort((a, b) => REIFE.indexOf(a) - REIFE.indexOf(b))[0]
    : null;
  return {
    stories: gefunden,
    fehlend,
    status,
    kriterien: gefunden.reduce((n, s) => n + (s.tests?.kriterien ?? s.kriterienCount ?? 0), 0),
    abgedeckt: gefunden.reduce((n, s) => n + (s.tests?.abgedeckt ?? 0), 0),
    rot: gefunden.some((s) => s.tests?.status === 'rot' || s.regression),
    blocker: gefunden.reduce((n, s) => n + (s.tickets?.blocker ?? 0), 0),
  };
}

const prozent = (teil: number, ganz: number) => (ganz ? Math.round((teil / ganz) * 100) : 0);

function AbdeckungsBalken({ abgedeckt, kriterien }: { abgedeckt: number; kriterien: number }) {
  return (
    <div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
        <div className="h-full bg-emerald-500" style={{ width: `${prozent(abgedeckt, kriterien)}%` }} />
      </div>
      <p className="mt-1 text-[11px] tabular-nums text-muted-foreground">
        {abgedeckt}/{kriterien} Kriterien mit Test
      </p>
    </div>
  );
}

/** Verteilung der Stories nach Status als ein gestapelter Balken. */
function StatusVerteilung({ stories }: { stories: StoryDto[] }) {
  const gesamt = stories.length;
  return (
    <div className="flex h-2 overflow-hidden rounded-full bg-muted" role="img" aria-label="Stories nach Status">
      {BALKEN_REIHENFOLGE.map((status) => {
        const anzahl = stories.filter((s) => s.status === status).length;
        return anzahl ? (
          <div
            key={status}
            title={`${STORY_STATUS_LABEL[status]}: ${anzahl}`}
            style={{ width: `${(anzahl / gesamt) * 100}%`, background: STATUS_FARBE[status] }}
          />
        ) : null;
      })}
    </div>
  );
}

function StoryChip({ story }: { story: StoryDto }) {
  return (
    <Link
      to={`/abnahme/stories/${story.id}`}
      title={`${story.title} — ${STORY_STATUS_LABEL[story.status]}`}
      className={`inline-flex rounded px-1.5 py-0.5 font-mono text-[11px] font-medium hover:ring-2 hover:ring-ring/40 ${STORY_STATUS_KLASSE[story.status]}`}
    >
      {story.key}
    </Link>
  );
}

function SchrittKnoten({ schritt, stories }: { schritt: AblaufSchritt; stories: Map<string, StoryDto> }) {
  const stand = standVon(schritt.stories, stories);
  return (
    <div
      className={`flex w-full flex-col gap-2 rounded-lg border bg-card p-3 sm:w-52 ${
        schritt.nebenweg ? 'border-dashed' : ''
      } ${stand.rot || stand.blocker ? 'border-red-400 dark:border-red-700' : ''}`}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${AKTEUR_KLASSE[schritt.akteur]}`}>
          {AKTEUR_LABEL[schritt.akteur]}
        </span>
        {schritt.nebenweg && <span className="text-[11px] text-muted-foreground">Nebenweg</span>}
      </div>
      <p className="text-sm font-medium leading-snug">{schritt.titel}</p>
      {stand.status && <StoryStatusBadge status={stand.status} />}
      <AbdeckungsBalken abgedeckt={stand.abgedeckt} kriterien={stand.kriterien} />
      {(stand.rot || stand.blocker > 0) && (
        <p className="inline-flex items-center gap-1 text-[11px] text-red-600 dark:text-red-400">
          <AlertTriangle className="h-3 w-3" />
          {[stand.rot ? 'Test rot' : null, stand.blocker ? `${stand.blocker} Blocker` : null]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
      <div className="mt-auto flex flex-wrap gap-1">
        {stand.stories.map((story) => (
          <StoryChip key={story.key} story={story} />
        ))}
        {stand.fehlend.map((key) => (
          <span
            key={key}
            title="Diesen Schlüssel gibt es im Katalog nicht (mehr)."
            className="rounded px-1.5 py-0.5 font-mono text-[11px] text-red-600 line-through dark:text-red-400"
          >
            {key}
          </span>
        ))}
      </div>
    </div>
  );
}

function storiesDes(ablauf: Ablauf, stories: Map<string, StoryDto>): StoryDto[] {
  const keys = [...new Set(ablauf.schritte.flatMap((s) => s.stories))];
  return keys.map((key) => stories.get(key)).filter((s): s is StoryDto => Boolean(s));
}

function AblaufAbschnitt({ ablauf, stories }: { ablauf: Ablauf; stories: Map<string, StoryDto> }) {
  const alle = storiesDes(ablauf, stories);
  const stand = standVon(
    alle.map((s) => s.key),
    stories,
  );
  const abgenommen = alle.filter((s) => s.status === 'abgenommen').length;
  const bereit = alle.filter((s) => s.status === 'bereit_zur_abnahme').length;
  const offen = alle.length - abgenommen - bereit;
  return (
    <section id={`ablauf-${ablauf.key}`} className="scroll-mt-4 space-y-4 rounded-xl border bg-card/50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 max-w-2xl">
          <h2 className="text-lg font-semibold">{ablauf.titel}</h2>
          <p className="text-sm text-muted-foreground">{ablauf.beschreibung}</p>
        </div>
        <dl className="flex gap-5 text-sm">
          {[
            ['Stories', alle.length],
            ['abgenommen', abgenommen],
            ['bereit', bereit],
            ['offen', offen],
            ['Testabdeckung', `${prozent(stand.abgedeckt, stand.kriterien)} %`],
          ].map(([label, wert]) => (
            <div key={label as string}>
              <dt className="text-xs text-muted-foreground">{label}</dt>
              <dd className="font-semibold tabular-nums">{wert}</dd>
            </div>
          ))}
        </dl>
      </div>
      <ol className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-stretch">
        {ablauf.schritte.map((schritt, index) => (
          <li key={schritt.titel} className="flex flex-col items-center gap-2 sm:flex-row sm:items-stretch">
            <SchrittKnoten schritt={schritt} stories={stories} />
            {index < ablauf.schritte.length - 1 && (
              <ArrowRight className="h-4 w-4 shrink-0 rotate-90 self-center text-muted-foreground sm:rotate-0" aria-hidden />
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * Abläufe: die wichtigsten Wege durchs System als Kette von Schritten, je Schritt
 * die Stories mit Status und Testabdeckung (docs/abnahme-plan.md, Abschnitt 15).
 *
 * So sieht man, wo ein ganzer Weg abnahmereif ist und wo er reißt — der Katalog
 * zeigt dasselbe nach Fachbereichen, hier nach dem, was ein Kunde oder das Office
 * nacheinander tut.
 */
export default function AblaeufePage() {
  const [katalog, setKatalog] = useState<KatalogDto | null>(null);

  useEffect(() => {
    abnahmeApi
      .katalog()
      .then(setKatalog)
      .catch((fehler: unknown) =>
        toast.error(fehler instanceof Error ? fehler.message : 'Der Katalog ließ sich nicht laden.'),
      );
  }, []);

  const stories = useMemo(() => {
    const map = new Map<string, StoryDto>();
    for (const epic of katalog?.epics ?? []) {
      if (epic.archived) continue;
      for (const story of epic.stories) if (!story.archived) map.set(story.key, story);
    }
    return map;
  }, [katalog]);

  const ohneAblauf = useMemo(() => {
    const zugeordnet = new Set(ABLAEUFE.flatMap((a) => a.schritte.flatMap((s) => s.stories)));
    return (katalog?.epics ?? [])
      .filter((epic) => !epic.archived)
      .map((epic) => ({
        epic,
        stories: epic.stories.filter((s) => !s.archived && !zugeordnet.has(s.key)),
      }))
      .filter((gruppe) => gruppe.stories.length > 0);
  }, [katalog]);

  if (!katalog) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Abläufe werden geladen …
      </div>
    );
  }

  return (
    <div className="viz-root mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Abläufe</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Die wichtigsten Wege durchs System, Schritt für Schritt. Jeder Schritt ist so weit wie seine schwächste
          Story; der Balken zeigt, wie viele Kriterien ein automatischer oder manueller Test belegt. Ein Klick auf
          einen Schlüssel öffnet die Story.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {ABLAEUFE.map((ablauf) => {
          const alle = storiesDes(ablauf, stories);
          const stand = standVon(
            alle.map((s) => s.key),
            stories,
          );
          return (
            <a
              key={ablauf.key}
              href={`#ablauf-${ablauf.key}`}
              className="space-y-2 rounded-lg border bg-card p-3 transition-colors hover:bg-muted/50"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-sm font-medium">{ablauf.titel}</span>
                {(stand.rot || stand.blocker > 0) && (
                  <AlertTriangle className="h-4 w-4 shrink-0 text-red-600" aria-label="Rot oder Blocker" />
                )}
              </div>
              <StatusVerteilung stories={alle} />
              <p className="text-xs text-muted-foreground">
                {alle.filter((s) => s.status === 'abgenommen').length} von {alle.length} abgenommen ·{' '}
                {prozent(stand.abgedeckt, stand.kriterien)} % Testabdeckung
              </p>
            </a>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {BALKEN_REIHENFOLGE.map((status) => (
          <span key={status} className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: STATUS_FARBE[status] }} />
            {STORY_STATUS_LABEL[status]}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-4 rounded-sm border border-dashed border-muted-foreground" /> Nebenweg
        </span>
      </div>

      {ABLAEUFE.map((ablauf) => (
        <AblaufAbschnitt key={ablauf.key} ablauf={ablauf} stories={stories} />
      ))}

      {ohneAblauf.length > 0 && (
        <details className="rounded-xl border p-4">
          <summary className="cursor-pointer text-sm font-medium">
            Stories ohne Ablauf ({ohneAblauf.reduce((n, g) => n + g.stories.length, 0)}) — Stammdaten, Verwaltung,
            Auswertungen
          </summary>
          <div className="mt-3 space-y-3">
            {ohneAblauf.map(({ epic, stories: liste }) => (
              <div key={epic.key}>
                <p className="mb-1 text-xs text-muted-foreground">{epic.title}</p>
                <div className="flex flex-wrap gap-1">
                  {liste.map((story) => (
                    <StoryChip key={story.key} story={story} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
